import { from, Observable } from 'rxjs';
import { noop } from 'lodash';
import { componentTreeSelectionChanged } from '@ardoq/component-selection';
import { colors } from '@ardoq/design-tokens';
import { enterPressed, moveHighlightRequested } from '@ardoq/dropdown-menu';
import { dispatchAction, reducer, streamReducer } from '@ardoq/rxbeach';
import { Locale } from '@ardoq/locale';
import { getOnMouseOverHandler } from '../componentSelector/getOnMouseOverHandler';
import { getPublishScrollHeight } from './routines/getPublishScrollHeight';
import { enhancedScopeDataStreamReducer } from './reducers/selectComponentStreamReducer';
import { getHandleEnterPressedRoutine } from './routines/getHandleEnterPressedRoutine';
import { getScrollHighlightedIntoViewRoutine } from './routines/getScrollHighlightedIntoViewRoutine';
import { actionNamespaceSelectParent } from '@ardoq/global-consts';
import { ComponentHierarchyState } from '../componentHierarchyTypes';
import { NavigatorConfiguration } from '../types';
import { navigatorLayoutOperations } from '../reducers/navigatorLayoutOperations';
import { navigatorSelectionOperations } from '../reducers/navigatorSelectionOperations';
import { navigatorOperations } from '../reducers/navigatorOperations';
import { newItemHovered } from '../actions';
import { navigatorTreeOperations } from '../reducers/navigatorTreeOperations';
import { enhancedScopeDataToHierarchyStreamNoDnD } from './enhancedScopeDataToHierarchyStreamNoDnD';
import { getSelectComponentHierarchySelection$ } from '../streams/selectComponentHierarchySelection$';
import {
  enhancedScopeData$,
  loadingScopeDataNotification$,
  loadScopeDataError$,
} from '../streams/selectParentScopeDataStream';
import { getHandleComponentSelectedRoutine } from './routines/getHandleComponentSelectedRoutine';
import { SingleSelectComponentItem } from './components/SingleSelectComponentItem';
import { SingleSelectWorkspaceItem } from './components/SingleSelectWorkspaceItem';
import { expandNodeInTree } from '../actions/navigatorActions';
import { getDebouncedHeightChangedDispatcher } from './getDebouncedHeightChangedDispatcher';

const parentComponentSelection$ = getSelectComponentHierarchySelection$(
  actionNamespaceSelectParent
);

export const getSelectParentConfiguration = (
  locale: Locale
): NavigatorConfiguration => {
  const selectParentComponent$: Observable<ComponentHierarchyState> =
    enhancedScopeDataToHierarchyStreamNoDnD({
      enhancedScopeData$,
      locale,
      isStrictSelection: true,
    });

  return {
    eventHandlers: {
      onComponentClick: componentId =>
        // The node is highlighted on hover, so just trigger enterPressed action.
        dispatchAction(
          enterPressed({ nodeId: componentId, withMetaKey: false }),
          actionNamespaceSelectParent
        ),
      onWorkspaceClick: workspaceId =>
        dispatchAction(
          enterPressed({ nodeId: workspaceId, withMetaKey: false }),
          actionNamespaceSelectParent
        ),
      onScenarioClick: noop,
      onWorkspaceCloseClick: noop,
      onScenarioCloseClick: noop,
      onComponentContextMenu: null,
      onWorkspaceContextmenu: null,
      onScenarioContextmenu: noop,
      onContextMenuClicked: noop,
      onMouseOver: getOnMouseOverHandler(),
    },
    additionalRoutines: [
      getPublishScrollHeight,
      getScrollHighlightedIntoViewRoutine,
      getHandleEnterPressedRoutine,
      getHandleComponentSelectedRoutine,
      // is this needed?
      // getSelectComponentSelectionChangedRoutine,
    ],
    externalReducers: [
      streamReducer(selectParentComponent$, enhancedScopeDataStreamReducer),
      streamReducer(
        from([{ isViewpointMode: true }]),
        navigatorLayoutOperations.setIsViewpointMode
      ),

      streamReducer(
        parentComponentSelection$,
        navigatorSelectionOperations.updateSelection
      ),
      streamReducer(
        loadingScopeDataNotification$,
        navigatorOperations.setDataLoadingMessage
      ),
      streamReducer(
        loadScopeDataError$,
        navigatorOperations.setDataErrorMessage
      ),
      reducer(newItemHovered, navigatorTreeOperations.newItemHovered),
      reducer(moveHighlightRequested, navigatorTreeOperations.moveHighlight),
      reducer(expandNodeInTree, navigatorLayoutOperations.expandNodeInTree),
    ],
    componentSelection$: parentComponentSelection$,
    rangeSelectionMode: 'noRangeSelection',
    actionNamespace: actionNamespaceSelectParent,
    showWorkspaceCloseButton: true,
    showStartCreateReferenceIcon: false,
    backgroundColor: colors.white950,
    components: {
      ComponentItem: SingleSelectComponentItem,
      WorkspaceItem: SingleSelectWorkspaceItem,
      ScenarioItem: () => null,
    },
    onViewDestroyed: () => {
      dispatchAction(
        componentTreeSelectionChanged({ treeSelection: [] }),
        actionNamespaceSelectParent
      );
    },
    isControlledContainerHeight: false,
    useClassNameForRootComponents: true,
    onRootResize: getDebouncedHeightChangedDispatcher(
      actionNamespaceSelectParent
    ),
  };
};
